import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminUser } from "@/lib/roles";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Plus, Search, FileDown, Printer, Pencil, Trash2, Crown, ExternalLink, ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import StatusPill from "@/components/StatusPill";
import LocationsSection from "@/components/LocationsSection";
import Pagination from "@/components/Pagination";
import { useResponsivePageSize } from "@/hooks/useResponsivePageSize";
import { downloadCsv, downloadAsPdf } from "@/lib/exporters";
import { BulkActions, SelectionCheckbox, SelectAllCheckbox } from "@/components/BulkActions";

const EMPTY = {
  name: "", company: "", email: "", phone: "", address: "", city: "", state: "", zip_code: "",
  lat: "", lng: "", photo_url: "", notes: "", position_title: "", other_position_title: "",
  balance: 0, status: "New", birthday: "", anniversary: "",
  endpoint_partner_id: "warehouse",
  has_multiple_locations: false,
  additional_locations: [],
  license_number: "",
  license_image_url: "",
  license_status: "missing",
  license_expires_at: "",
};

const LICENSE_BADGE = {
  verified: { label: "Verified", className: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
  pending:  { label: "Pending",  className: "bg-amber-100  text-amber-800  border border-amber-200"  },
  rejected: { label: "Rejected", className: "bg-red-100    text-red-800    border border-red-200"    },
  missing:  { label: "Missing",  className: "bg-red-100    text-red-800    border border-red-200"    },
};

function LicenseBadge({ status, expiresAt }) {
  const key = (status || "missing").toLowerCase();
  const s = LICENSE_BADGE[key] || LICENSE_BADGE.missing;
  let expiryChip = null;
  if (expiresAt) {
    const daysLeft = Math.round((new Date(expiresAt).getTime() - Date.now()) / 86400000);
    if (daysLeft < 0) {
      expiryChip = <span className="ml-1 rounded px-1 py-[1px] text-[9px] font-bold uppercase tracking-widest bg-red-600 text-white">Expired</span>;
    } else if (daysLeft <= 30) {
      expiryChip = <span className="ml-1 rounded px-1 py-[1px] text-[9px] font-bold uppercase tracking-widest bg-orange-500 text-white">{daysLeft}d</span>;
    }
  }
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest ${s.className}`}
      data-testid={`license-badge-${key}`}
      title={expiresAt ? `Expires ${String(expiresAt).slice(0, 10)}` : undefined}
    >
      {s.label}
      {expiryChip}
    </span>
  );
}

export default function ClientsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = isAdminUser(user);
  const canManage = isAdmin || user?.role === "personnel" || user?.role === "distributing_partner";
  const [clients, setClients] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [partners, setPartners] = useState([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("clients-sort") || "name");
  const [sortDirection, setSortDirection] = useState(() => localStorage.getItem("clients-sort-direction") || "asc");
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const PAGE_SIZE = useResponsivePageSize(10, 50);

  const load = async () => {
    const [c, p] = await Promise.all([
      api.get("/clients").then(r => r.data),
      api.get("/endpoint-partners").then(r => r.data).catch(() => []),
    ]);
    setClients(c); setPartners(p);
  };
  useEffect(() => { load(); }, []);

  const partnerName = useCallback((id) => partners.find(p => p.id === id)?.name || "—", [partners]);

  const [licenseFilter, setLicenseFilter] = useState("all"); // all | missing | pending | verified | rejected | expiring

  useEffect(() => { localStorage.setItem("clients-sort", sortBy); localStorage.setItem("clients-sort-direction", sortDirection); }, [sortBy, sortDirection]);
  const chooseSort = (field) => {
    if (sortBy === field) setSortDirection((direction) => direction === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortDirection(["spend", "txns", "recent"].includes(field) ? "desc" : "asc"); }
  };
  const SortHeader = ({ field, children, className = "" }) => {
    const Icon = sortBy === field ? (sortDirection === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
    return <th className={className}><button type="button" onClick={() => chooseSort(field)} className="inline-flex items-center gap-1 hover:text-[var(--ink)] transition-colors" data-testid={`clients-sort-${field}`}>{children}<Icon size={12} /></button></th>;
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const phoneQuery = search.replace(/\D/g, "");
    let list = q
      ? clients.filter((c) => c.name?.toLowerCase().includes(q) || c.company?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || (phoneQuery.length === 10 && String(c.phone || "").replace(/\D/g, "").includes(phoneQuery)))
      : clients;
    if (licenseFilter !== "all") {
      list = list.filter((c) => {
        const st = (c.license_status || "missing").toLowerCase();
        if (licenseFilter === "expiring") {
          if (!c.license_expires_at) return false;
          const daysLeft = Math.round((new Date(c.license_expires_at).getTime() - Date.now()) / 86400000);
          return daysLeft <= 30;
        }
        return st === licenseFilter;
      });
    }
    const sorted = [...list];
    sorted.sort((a, b) => {
      let comparison = 0;
      if (sortBy === "spend") comparison = Number(a.lifetime_spend || 0) - Number(b.lifetime_spend || 0);
      else if (sortBy === "txns") comparison = Number(a.transaction_count || 0) - Number(b.transaction_count || 0);
      else if (sortBy === "recent") comparison = (a.last_visit || "").localeCompare(b.last_visit || "");
      else if (sortBy === "partner") comparison = partnerName(a.endpoint_partner_id).localeCompare(partnerName(b.endpoint_partner_id));
      else if (sortBy === "status") comparison = (a.status || "").localeCompare(b.status || "");
      else comparison = (a.name || "").localeCompare(b.name || "");
      return sortDirection === "asc" ? comparison : -comparison;
    });
    return sorted;
  }, [clients, search, sortBy, sortDirection, licenseFilter, partnerName]);

  useEffect(() => { setPage(0); }, [search, sortBy, sortDirection, licenseFilter]);

  // Precompute license counts for the chip counters
  const licenseCounts = useMemo(() => {
    const acc = { all: clients.length, missing: 0, pending: 0, verified: 0, rejected: 0, expiring: 0 };
    for (const c of clients) {
      const st = (c.license_status || "missing").toLowerCase();
      if (acc[st] !== undefined) acc[st] += 1;
      if (c.license_expires_at) {
        const daysLeft = Math.round((new Date(c.license_expires_at).getTime() - Date.now()) / 86400000);
        if (daysLeft <= 30) acc.expiring += 1;
      }
    }
    return acc;
  }, [clients]);

  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  // VIP list: top 10 by lifetime_spend (>= $5k qualifier)
  const vips = useMemo(
    () => [...clients]
      .filter(c => (c.lifetime_spend || 0) >= 5000)
      .sort((a, b) => (b.lifetime_spend || 0) - (a.lifetime_spend || 0))
      .slice(0, 10),
    [clients]
  );

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "", company: c.company || "", email: c.email || "", phone: c.phone || "",
      address: c.address || "", city: c.city || "", state: c.state || "", zip_code: c.zip_code || "",
      lat: c.lat ?? "", lng: c.lng ?? "",
      photo_url: c.photo_url || "", notes: c.notes || "", position_title: c.position_title || "", other_position_title: "",
      balance: c.balance ?? 0, status: c.status || "New",
      birthday: c.birthday || "", anniversary: c.anniversary || "",
      endpoint_partner_id: c.endpoint_partner_id || "warehouse",
      has_multiple_locations: !!c.has_multiple_locations,
      additional_locations: c.additional_locations || [],
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.endpoint_partner_id) { toast.error("Distributing Partner is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, position_title: form.position_title === "Other" ? form.other_position_title.trim().replace(/\b\w/g, (letter) => letter.toUpperCase()) : form.position_title,
        lat: form.lat === "" ? null : Number(form.lat),
        lng: form.lng === "" ? null : Number(form.lng),
        balance: Number(form.balance) || 0,
        birthday: form.birthday || null, anniversary: form.anniversary || null,
      };
      if (editing) { await api.put(`/clients/${editing.id}`, payload); toast.success("Client updated"); }
      else { await api.post("/clients", payload); toast.success("Client added"); }
      setForm(EMPTY); setEditing(null); setOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/clients/${c.id}`); toast.success("Client deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    try { await downloadCsv("/clients/export.csv", "clients.csv"); toast.success("CSV downloaded"); }
    catch { toast.error("Export failed"); }
  };

  return (
    <div className="p-3 sm:p-6 lg:p-10 max-w-full" data-testid="clients-page">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="label-eyebrow">Customer base</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-1">Clients</h1>
          <p className="text-gray-500 mt-1">{clients.length} total · onboarding, orders & balances at a glance</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
            <Input className="pl-8 w-64" placeholder="Search name, company, email…"
                   value={search} onChange={(e) => setSearch(e.target.value)}
                   data-testid="clients-search-input" />
          </div>
          <select className="border border-[var(--border)] rounded-md px-3 h-10 text-sm bg-white" value={sortBy} onChange={e => chooseSort(e.target.value)} data-testid="clients-sort">
            <option value="name">Sort · Name (A-Z)</option>
            <option value="partner">Sort · Distributing Partner</option>
            <option value="status">Sort · Status</option>
            <option value="spend">Sort · Lifetime spend</option>
            <option value="txns">Sort · Transactions</option>
            <option value="recent">Sort · Recent visit</option>
          </select>
          {isAdmin && (
            <>
              <Button variant="outline" onClick={exportCsv} data-testid="export-clients-csv"><FileDown size={14} className="mr-1" /> CSV</Button>
              <label className="inline-flex">
                <input type="file" accept=".csv" className="hidden" data-testid="import-clients-csv"
                  onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; const fd = new FormData(); fd.append("file", f);
                    try { const { data } = await api.post("/clients/import.csv", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Imported ${data.created} clients`); load(); }
                    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } e.target.value = ""; }} />
                <span className="inline-flex items-center cursor-pointer px-4 py-2 border border-[var(--border)] rounded-md text-sm font-medium hover:bg-[var(--bg-soft)]">Import CSV</span>
              </label>
              <Button variant="outline" onClick={() => downloadAsPdf("clients-print", "clients.pdf")} data-testid="export-clients-pdf"><Printer size={14} className="mr-1" /> PDF</Button>
            </>
          )}
          {canManage && (
              <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
                <SheetTrigger asChild>
                  <Button className="btn-brand" onClick={openNew} data-testid="add-client-button"><Plus size={16} className="mr-1" /> Add client</Button>
                </SheetTrigger>
                <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="add-client-sheet">
                  <SheetHeader><SheetTitle>{editing ? "Edit client" : "New client"}</SheetTitle></SheetHeader>
                  <form onSubmit={submit} className="space-y-4 mt-6" data-testid="add-client-form">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" /></div>
                      <div><Label>Title</Label><select value={form.position_title} onChange={(e) => setForm({ ...form, position_title: e.target.value })} className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="client-title-select"><option value="">Select title</option>{["Hair/Salon Stylist","Braider/Loctition","Master Groomer/Barber","Owner","Manager","Other"].map((title) => <option key={title}>{title}</option>)}</select>{form.position_title === "Other" && <Input className="mt-2" value={form.other_position_title} onChange={(e) => setForm({ ...form, other_position_title: e.target.value })} placeholder="Enter title" data-testid="client-other-title-input" />}</div>
                      <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="form-company" /></div>
                      <div className="col-span-2">
                        <Label>Distributing Partner *</Label>
                        <Select value={form.endpoint_partner_id || undefined} onValueChange={(v) => setForm({ ...form, endpoint_partner_id: v })}>
                          <SelectTrigger data-testid="form-partner"><SelectValue placeholder="Select distributor" /></SelectTrigger>
                          <SelectContent>
                            {partners.map(p => <SelectItem key={p.id} value={p.id}>{p.name} {p.company ? `(${p.company})` : ""}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="form-email" /></div>
                      <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="form-phone" /></div>
                      <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="form-address" /></div>
                      <div><Label>Zip code</Label><Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} data-testid="form-zip" /></div>
                      <div><Label>Balance</Label><Input type="number" step="0.01" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} data-testid="form-balance" /></div>
                      <div><Label>Latitude</Label><Input value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} placeholder="40.7128" data-testid="form-lat" /></div>
                      <div><Label>Longitude</Label><Input value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} placeholder="-74.0060" data-testid="form-lng" /></div>
                      <div className="col-span-2"><Label>Photo URL</Label><Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} data-testid="form-photo" placeholder="https://…" /></div>
                      <div><Label>Birthday</Label><Input type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} data-testid="form-birthday" /></div>
                      <div><Label>Anniversary</Label><Input type="date" value={form.anniversary} onChange={(e) => setForm({ ...form, anniversary: e.target.value })} data-testid="form-anniversary" /></div>
                      <div className="col-span-2 rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-widest text-amber-800">Cosmetology license</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">License number</Label>
                            <Input
                              value={form.license_number || ""}
                              onChange={(e) => setForm({ ...form, license_number: e.target.value })}
                              placeholder="e.g. TX-COS-123456"
                              data-testid="form-license-number"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">License image URL</Label>
                            <Input
                              value={form.license_image_url || ""}
                              onChange={(e) => setForm({ ...form, license_image_url: e.target.value })}
                              placeholder="/api/client-licenses/… or https://…"
                              data-testid="form-license-image-url"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Status</Label>
                            <Select
                              value={form.license_status || "missing"}
                              onValueChange={(v) => setForm({ ...form, license_status: v })}
                            >
                              <SelectTrigger data-testid="form-license-status"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="missing">Missing</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="verified">Verified</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Expires on</Label>
                            <Input
                              type="date"
                              value={(form.license_expires_at || "").slice(0, 10)}
                              onChange={(e) => setForm({ ...form, license_expires_at: e.target.value })}
                              data-testid="form-license-expires-at"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="form-notes" /></div>
                      <LocationsSection value={form} onChange={(patch) => setForm({ ...form, ...patch })} />
                    </div>
                    <Button className="btn-brand w-full" disabled={saving} data-testid="form-submit-button">{saving ? "Saving…" : (editing ? "Save changes" : "Create client")}</Button>
                  </form>
                </SheetContent>
              </Sheet>
          )}
        </div>
      </header>

      {/* License filter chips — fast triage for HQ */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="license-filter-chips">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 mr-1">License:</span>
        {[
          { key: "all",      label: "All",      tone: "bg-white text-gray-700 border-gray-300 hover:bg-gray-50" },
          { key: "missing",  label: "Missing",  tone: "bg-red-50 text-red-800 border-red-200 hover:bg-red-100" },
          { key: "pending",  label: "Pending",  tone: "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100" },
          { key: "verified", label: "Verified", tone: "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100" },
          { key: "rejected", label: "Rejected", tone: "bg-red-50 text-red-800 border-red-200 hover:bg-red-100" },
          { key: "expiring", label: "Expiring ≤30d", tone: "bg-orange-50 text-orange-800 border-orange-200 hover:bg-orange-100" },
        ].map((chip) => {
          const active = licenseFilter === chip.key;
          return (
            <button
              key={chip.key}
              type="button"
              onClick={() => setLicenseFilter(chip.key)}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-widest transition-colors ${
                active ? "ring-2 ring-offset-1 ring-[var(--brand)] " + chip.tone : chip.tone
              }`}
              data-testid={`license-filter-${chip.key}`}
            >
              {chip.label}
              <span className={`inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] tabular-nums ${active ? "bg-white/70" : "bg-white/50"}`}>
                {licenseCounts[chip.key] || 0}
              </span>
            </button>
          );
        })}
      </div>

      {vips.length > 0 && (
        <Card className="p-5 mb-6 border-amber-200 bg-gradient-to-br from-amber-50 to-white" data-testid="vips-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Crown size={16} className="text-amber-500" />
              <span className="label-eyebrow">VIPs · $5k+ lifetime spend</span>
            </div>
            <span className="text-xs text-gray-500">{vips.length} shown</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2">
            {vips.map((c, i) => (
              <button key={c.id}
                      onClick={() => navigate(`/clients/${c.id}`)}
                      className="group text-left p-3 rounded-lg bg-white border border-amber-100 hover:border-amber-400 hover:shadow-sm transition"
                      data-testid={`vip-card-${c.id}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${i < 3 ? "bg-amber-400 text-white" : "bg-amber-100 text-amber-700"}`}>
                    {i + 1}
                  </div>
                  <span className="font-semibold text-sm truncate capitalize group-hover:text-[var(--brand)]">{c.name}</span>
                </div>
                <div className="mt-1 flex justify-between items-baseline">
                  <span className="text-lg font-bold text-emerald-600 tabular-nums">
                    ${(c.lifetime_spend || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                  <span className="text-xs text-gray-500 tabular-nums">{c.transaction_count || 0} txns</span>
                </div>
                {c.last_visit && <div className="text-[10px] text-gray-400 mt-0.5">last visit {c.last_visit}</div>}
              </button>
            ))}
          </div>
        </Card>
      )}

      <Card className="overflow-hidden mb-6" id="clients-print">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="clients-table">
            <thead className="bg-[var(--bg-soft)] text-xs uppercase tracking-wider text-gray-500 sticky top-0">
              <tr>
                <th className="p-3 w-10"><SelectAllCheckbox records={paged} selectedIds={selectedIds} setSelectedIds={setSelectedIds} /></th>
                <th className="text-left p-3 hidden md:table-cell">Photo</th>
                <th className="text-left p-3 sticky left-0 bg-[var(--bg-soft)] z-20 min-w-[160px] border-r border-[var(--border)] md:border-r-0">Name / Contact</th>
                <th className="text-left p-3">Company</th>
                <SortHeader field="partner" className="text-left p-3">Distributing Partner</SortHeader>
                <SortHeader field="status" className="text-left p-3">Status</SortHeader>
                <th className="text-left p-3">License</th>
                <SortHeader field="spend" className="text-right p-3">Lifetime spend</SortHeader>
                <th className="text-right p-3">Txns</th>
                <SortHeader field="recent" className="text-left p-3">Last visit</SortHeader>
                <th className="text-left p-3">Sub-Store</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((c) => {
                const isVip = (c.lifetime_spend || 0) >= 5000;
                return (
                <tr key={c.id}
                    onClick={() => navigate(`/clients/${c.id}`)}
                    className={`table-row group border-t border-[var(--border)] cursor-pointer hover:bg-gray-50 ${isVip ? "bg-amber-50/50" : ""}`}
                    data-testid={`client-row-${c.id}`}>
                  <td className="p-3" onClick={(event) => event.stopPropagation()}><SelectionCheckbox checked={selectedIds.includes(c.id)} onChange={(checked) => setSelectedIds(checked ? [...selectedIds, c.id] : selectedIds.filter((id) => id !== c.id))} testId={`select-row-checkbox-${c.id}`} label={`Select ${c.name}`} /></td>
                  <td className="p-3 hidden md:table-cell">
                    <div className={`w-10 h-10 rounded-full overflow-hidden ${isVip ? "ring-2 ring-amber-400" : "bg-gray-200"}`}>
                      {c.photo_url ? <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover" /> :
                        <div className="w-full h-full flex items-center justify-center text-xs text-gray-500 bg-gray-200">{c.name?.[0]}</div>}
                    </div>
                  </td>
                  <td className={`p-3 sticky left-0 z-10 min-w-[160px] border-r border-[var(--border)] md:border-r-0 ${isVip ? "bg-amber-50/50" : "bg-white"} group-hover:bg-gray-50`}>
                    <div className="font-medium capitalize flex items-center gap-1">
                      {isVip && <Crown size={12} className="text-amber-500" data-testid="vip-badge" />}
                      {c.name}
                    </div>
                    <div className="text-xs text-gray-500 normal-case truncate max-w-xs">
                      {c.email || "—"}{c.phone ? ` · ${c.phone}` : ""}
                    </div>
                  </td>
                  <td className="p-3 text-gray-600 capitalize">{c.company || "—"}</td>
                  <td className="p-3 text-gray-600 capitalize">{partnerName(c.endpoint_partner_id)}</td>
                  <td className="p-3"><StatusPill status={c.status} /></td>
                  <td className="p-3"><LicenseBadge status={c.license_status} expiresAt={c.license_expires_at} /></td>
                  <td className={`p-3 text-right tabular-nums ${isVip ? "font-bold text-emerald-600" : "font-semibold text-emerald-600"}`}>
                    ${(c.lifetime_spend || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="p-3 text-right tabular-nums text-gray-600">{c.transaction_count || 0}</td>
                  <td className="p-3 text-xs text-gray-500">{c.last_visit || "—"}</td>
                  <td className="p-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {c.slug ? (
                      <Link
                        to={`/shop/${c.slug}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--brand)] hover:underline"
                        data-testid={`visit-shop-${c.id}`}
                      >
                        <ExternalLink size={11} strokeWidth={2.5} /> Visit shop
                      </Link>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <Link to={`/clients/${c.id}`} className="text-[var(--brand)] hover:underline mr-3 text-xs" data-testid={`view-client-${c.id}`}>View</Link>
                    {canManage && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}><Pencil size={14} /></Button>
                        {canManage && (
                          <Button variant="ghost" size="sm" onClick={() => remove(c)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-client-${c.id}`}><Trash2 size={14} /></Button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );})}
              {paged.length === 0 && (
                <tr><td colSpan={11} className="p-10 text-center text-gray-400">No clients match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={filtered.length} pageSize={PAGE_SIZE} testid="clients-pagination" />
      </Card>
      <BulkActions section="clients" records={paged} selectedIds={selectedIds} setSelectedIds={setSelectedIds} onComplete={load} />
    </div>
  );
}
